import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ISelectOption, ITooltipProps } from '@private/components';

import AutocompleteSelect from 'components/Controls/AutocompleteSelect';
import { useDataGridContext } from 'components/Grids/DataGrid/DataGridContext';
import { useFilterContext } from 'components/Grids/DataGrid/Filters/FilterContext';
import { getSelectConfig } from 'components/Grids/DataGrid/Filters/Sidebar/components/FilterItem';
import AdditionalCondition from 'components/Grids/DataGrid/Filters/Sidebar/components/FilterItem/AdditionalCondition';
import FilterComponent, {
  isDatePickerType,
} from 'components/Grids/DataGrid/Filters/Sidebar/components/FilterItem/FilterComponent';
import { StyledHighlightedText } from 'components/Grids/DataGrid/Filters/Sidebar/components/FilterItem/styled';
import {
  IconContainer,
  Operation,
  OperationInfo,
} from 'components/Grids/DataGrid/Filters/Sidebar/components/FilterOperationButton/styled';
import { getFilterValueByOperation } from 'components/Grids/DataGrid/Filters/useFiltration';
import {
  FILTRATION_ICONS,
  INTL_MAPPING,
} from 'components/Grids/DataGrid/Filters/utils/constants';
import { getStrategies } from 'components/Grids/DataGrid/Filters/utils/helpers';
import {
  FILTER_OPERATIONS,
  IAppliedFilter,
  ISelectFilterProps,
} from 'components/Grids/DataGrid/Filters/utils/types';
import Popover from 'components/Popover';
import Typography from 'components/Typography';
import { KEYBOARD_KEYS } from 'constants/common';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useAppSelector } from 'hooks/storeHooks';
import { INullable } from 'utils/types';

import { StyledIconButton } from '../styled';

import { Actions, ApplyButton, CancelButton, Container, FiltersWrapper } from './styled';

interface IFilterButtonProps {
  columnKey: string;
  selectedOperation?: FILTER_OPERATIONS | null;
  showSelected?: boolean;
}

const getInitialFilter = (
  columnKey: string,
  appliedFilter: IAppliedFilter,
): ISelectFilterProps => {
  const columnFilter = appliedFilter[columnKey];

  return {
    columnKey,
    ...columnFilter,
  };
};

const getPopoverPlacement = (
  node: INullable<HTMLElement>,
): 'bottom-end' | 'bottom-start' => {
  const popoverPlacement = node?.getBoundingClientRect().x || 0;
  const windowHalf = window.innerWidth / 2;

  if (popoverPlacement > windowHalf) {
    return 'bottom-end';
  }

  return 'bottom-start';
};

const isFilterDefined = (filter: ISelectFilterProps) => {
  return filter.value !== undefined && filter.value !== '';
};

let calculateTooltipPosition: (() => void) | undefined;

const FilterButton = ({
  columnKey,
  selectedOperation,
  showSelected = true,
}: IFilterButtonProps) => {
  const { t } = useTranslation();
  const { filters } = useDataGridContext();
  const {
    config: {
      betweenList,
      defaultLikesFirstFields,
      filtersSections,
      filtersSectionsMap,
      intlPrefix,
    },
    conflictColumns,
    filters: appliedFilters,
    isSidebarOpen,
    onApply,
    onSelectFilter,
    onSelectOperation,
    removeFilter,
    sidebarFilters,
  } = useFilterContext();

  const hasConflict = conflictColumns.includes(columnKey);

  const filterOperationsSortStrategy = useAppSelector(
    (state) => state.userConfiguration.filterOperationsSortStrategy,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [canFilter, setCanFilter] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const columnFilter = useMemo(
    () => getInitialFilter(columnKey, sidebarFilters),
    [columnKey, sidebarFilters],
  );

  const sectionKey = filtersSectionsMap?.[columnKey];
  const filterSettings = sectionKey
    ? filtersSections?.get(sectionKey)?.get(columnKey)
    : undefined;

  const isDatePicker = isDatePickerType(filterSettings?.type);

  const onOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onClearFilter = useCallback(() => {
    removeFilter({ columnKey, resetCanFilter: true });
    setCanFilter(true);
  }, [columnKey, removeFilter]);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onApplyFilter = useCallback(() => {
    setCanFilter(false);
    onClose();
    if (
      sidebarFilters[columnFilter.columnKey] ||
      appliedFilters?.[columnFilter.columnKey]
    ) {
      onApply({
        ...(appliedFilters as IAppliedFilter),
        [columnFilter.columnKey]: sidebarFilters[columnFilter.columnKey]!,
      });
    }
  }, [onApply, onClose, appliedFilters, sidebarFilters, columnFilter]);

  const onSelectFilterOperation = useCallback(
    (value: unknown) => {
      if (value !== undefined) {
        onSelectOperation({
          ...columnFilter,
          operation: value as FILTER_OPERATIONS,
        });

        const newValue = getFilterValueByOperation(
          columnFilter,
          value as FILTER_OPERATIONS,
        );

        if (newValue !== undefined && newValue !== null) {
          return setCanFilter(true);
        }

        setCanFilter(false);
      }
    },
    [columnFilter, onSelectOperation],
  );

  const onSelectColumnFilter = useCallback(
    (props: ISelectFilterProps) => {
      onSelectFilter({ ...props, immediateApply: isDatePicker });
      setCanFilter(!isDatePicker);
      setIsOpen(!isDatePicker);
    },
    [isDatePicker, onSelectFilter],
  );

  const onToggleFilterValue = useCallback(() => {
    setCanFilter(true);
  }, []);

  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      const { key } = e;

      if (key === KEYBOARD_KEYS.ENTER && canFilter && !hasConflict && isOpen) {
        e.preventDefault();
        onApplyFilter();
      } else if (key === KEYBOARD_KEYS.ESCAPE && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keyup', onKeyPress);

    return () => {
      document.removeEventListener('keyup', onKeyPress);
    };
  }, [onApplyFilter, canFilter, hasConflict, isOpen, sidebarFilters]);

  const { isMultiSelect, isSelect } = useMemo(
    () =>
      getSelectConfig({
        multiSelect: !!filterSettings?.multiSelect,
        operation: columnFilter?.operation,
        type: filterSettings?.type,
      }),
    [columnFilter?.operation, filterSettings?.multiSelect, filterSettings?.type],
  );

  const operations = useMemo(() => {
    const currentFilter = filters?.[columnKey];

    if (!currentFilter) {
      return [];
    }

    return getStrategies({
      betweenList,
      columnKey,
      defaultLikesFirstFields,
      filterOperationsSortStrategy,
      isSelect: isSelect || currentFilter?.isRequestFilter,
      listOperations: currentFilter?.listOperations,
      searchNullable: currentFilter?.searchNullable,
      type: currentFilter?.type,
    });
  }, [
    betweenList,
    columnKey,
    filterOperationsSortStrategy,
    filters,
    isSelect,
    defaultLikesFirstFields,
  ]);

  const operationList = useMemo(() => {
    return operations.map<ISelectOption>((operation, index) => {
      const currentSelected = selectedOperation
        ? selectedOperation === operation
        : !index;
      const isSelected = showSelected ? currentSelected : false;

      return {
        component: (
          <Operation
            $isSelected={isSelected}
            $variant='column'
            data-testid={`operation-${operation}`}
            key={operation}
          >
            <OperationInfo>
              <IconContainer>{FILTRATION_ICONS[operation]}</IconContainer>
              <Typography size='sm' variant='regular'>
                {t(INTL_MAPPING[operation] as never)}
              </Typography>
            </OperationInfo>
            {isSelected && <ICONS_MAP.Check width={12} />}
          </Operation>
        ),
        label: t(INTL_MAPPING[operation] as never),
        value: operation,
      };
    });
  }, [operations, selectedOperation, showSelected, t]);

  useLayoutEffect(() => {
    if (calculateTooltipPosition) {
      calculateTooltipPosition();
    }
  }, [columnFilter.operation]);

  const renderPopoverContent: ITooltipProps['component'] = (calculatePosition) => {
    calculateTooltipPosition = calculatePosition;

    const [firstOperation] = operations;

    return (
      <Container>
        <AutocompleteSelect
          enableRemoveButton={false}
          label={t('common.filters.operator')}
          onChange={onSelectFilterOperation}
          options={operationList}
          size='sm'
          value={columnFilter.operation || firstOperation}
        />
        <FiltersWrapper>
          <StyledHighlightedText>
            {t(`${intlPrefix}.${columnKey}` as never)}
          </StyledHighlightedText>
          {filterSettings && (
            <>
              <FilterComponent
                appliedFilter={columnFilter}
                columnKey={columnKey}
                fallbackOperation={firstOperation}
                isRequestFilter={filterSettings.isRequestFilter}
                isSelect={isSelect}
                multiSelect={isMultiSelect}
                onSelectColumnFilter={onSelectColumnFilter}
                onToggleFilterValue={onToggleFilterValue}
                type={filterSettings.type}
                variant='column'
              />
              <AdditionalCondition
                additionalCondition={columnFilter.additionalCondition}
                columnKey={columnKey}
                currentOperation={columnFilter.operation || firstOperation}
                isMultiSelect={isMultiSelect}
                isRequestFilter={filterSettings.isRequestFilter}
                isSelect={isSelect}
                listOperations={filterSettings.listOperations}
                onSelectColumnFilter={onSelectColumnFilter}
                onToggle={onToggleFilterValue}
                onToggleFilterValue={onToggleFilterValue}
                type={filterSettings.type}
                variant='column'
              />
            </>
          )}
        </FiltersWrapper>
        {!isDatePicker && (
          <Actions>
            <CancelButton
              disabled={!isFilterDefined(columnFilter)}
              onClick={onClearFilter}
              testIds={{ container: 'clearFilterButton' }}
            >
              {t('common.filters.clearFilter')}
            </CancelButton>
            <ApplyButton
              disabled={!canFilter || hasConflict}
              onClick={onApplyFilter}
              testIds={{ container: 'applyFilterButton' }}
            >
              {t('common.filters.filter')}
            </ApplyButton>
          </Actions>
        )}
      </Container>
    );
  };

  return (
    <div ref={containerRef}>
      <Popover
        component={renderPopoverContent}
        disabled={isSidebarOpen}
        onClose={onClose}
        onOpen={onOpen}
        open={isOpen}
        placement={getPopoverPlacement(containerRef.current)}
        positionGap={4}
      >
        <StyledIconButton
          disabled={isSidebarOpen}
          iconSize={12}
          isActive={isOpen}
          size='sm'
          startIcon={<ICONS_MAP.Filters />}
          testIds={{ container: 'columnFilterButton' }}
          variant='icon'
        />
      </Popover>
    </div>
  );
};

export default appReactMemo(FilterButton);
